import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from "react-router-dom";
import toast from 'react-hot-toast';
import { Formik } from "formik";
import * as yup from "yup";
import { useTheme, useMediaQuery, Box, Typography,
  CircularProgress, TextField, Button,
} from '@mui/material';
import LogoutIcon from '@mui/icons-material/Logout';
import { LAYOUT_HEIGHT } from 'state/constants';
import api from 'state/axios';
import FlexBetween from 'components/FlexBetween';
import FlexCenter from 'components/FlexCenter';
import Header from 'components/Header';
import StyledButton from 'components/StyledButton';


const CrewMember = () => { 
    const navigate = useNavigate();
    const user = useSelector((state) => state.auth.user);
    const crewCode = user.crewCode;
    const theme = useTheme();    
    const isDesktop = useMediaQuery(theme.breakpoints.up('lg'));

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);    
    const [crewMember, setCrewMember] = useState({});

    const checkoutSchema = yup.object().shape({
        crewCode: yup.string().required("required"),
        crewName: yup.string().required("required"),
        email: yup.string().email("invalid email").required("required"),
        password: yup.string().required("required"),
        confirmPassword: yup.string()
            .oneOf([yup.ref('password'), null], 'Passwords must match')
            .required("required"),
    });

    const initialValues = {        
        crewCode: crewMember.crewCode || '',
        crewName: crewMember.crewName || '',        
        email: crewMember.email || '',        
        contactTel: crewMember.contactTel || '',        
        password: '',
        confirmPassword: '',
    };        

    useEffect(() => {        
        const fetchCrewMember  = async () => {
            try {
                setLoading(true);
                if (crewCode) {
                    const response = await api.get(`/crewmembers/read/${crewCode}`);
                    
                    setCrewMember(response.data); 
                }                
            } catch (error) {
                setError(error);
            } finally {
                setLoading(false);
            }
        };

        fetchCrewMember();
    }, [crewCode]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleFormSubmit = async (values, onSubmitProps) => {        
        try {            
            const url = crewCode ? `/crewmembers/update/${crewCode}` : '/crewmembers/new';
            const method = crewCode ? 'put' : 'post';
            const response = await api[method](url, values);
    
            if (response.status === 200 || response.status === 201) {
                toast.success('Crew Member updated successfully');
                onSubmitProps.resetForm();
                navigate("/");
            }
        } catch (error) {
            const errorMessage = error.response ? error.response.data.message : 'An error occurred';
            toast.error(errorMessage);
            setError(error);
        }
    };
    
    
    return (
        <Box className="main-container-box">             
            <FlexBetween>
                <Header 
                    title="Crew Member" 
                    subtitle={`${loading ? 'Loading...' : crewCode ? 'Update' : 'Create'}`}
                />
                {!loading && (
                    <Box display="flex" alignItems="center" gap={1}>
                        <StyledButton 
                            onClick={() => navigate("/login")}
                        >
                            <LogoutIcon />
                        </StyledButton>
                    </Box>
                )}
            </FlexBetween>
            {loading ? (
                <FlexCenter height={LAYOUT_HEIGHT}>
                    <CircularProgress />
                </FlexCenter>
            ) : error ? (
                <FlexCenter height={LAYOUT_HEIGHT}>
                    <Typography color="error">{error?.message}</Typography>
                </FlexCenter>
            ) : (
                <Box height={LAYOUT_HEIGHT} sx={{ overflowY: 'auto' }}>
                    <Formik
                        initialValues={initialValues}
                        enableReinitialize={true}
                        validationSchema={checkoutSchema}
                        onSubmit={handleFormSubmit}
                    >
                        {({
                            values,
                            errors,
                            touched,
                            handleBlur,
                            handleChange,
                            handleSubmit,
                        }) => (
                        <form onSubmit={handleSubmit}>
                            <Box
                                display="grid"
                                gap={2}
                                gridTemplateColumns="repeat(12, minmax(0, 1fr))"
                                sx={{
                                    "& > div": { gridColumn: isDesktop ? undefined : "span 12" },
                                }}             
                            >
                                <TextField
                                    variant="filled"
                                    type="text"
                                    label="Crew Code"
                                    value={values.crewCode}
                                    name="crewCode"
                                    error={!!touched.crewCode && !!errors.crewCode}
                                    helperText={touched.crewCode && errors.crewCode}
                                    slotProps={{ input: { readOnly: true } }}
                                    onBlur={handleBlur}
                                    onChange={handleChange}
                                    sx={{ gridColumn: "span 6" }}                
                                />
                                <TextField
                                    variant="filled"
                                    type="text"
                                    label="Crew Name"
                                    value={values.crewName}
                                    name="crewName"
                                    error={!!touched.crewName && !!errors.crewName}
                                    helperText={touched.crewName && errors.crewName}
                                    onBlur={handleBlur}
                                    onChange={handleChange}
                                    slotProps={{ input: { readOnly: true } }}
                                    sx={{ gridColumn: "span 6" }}
                                />                                                                                             
                                <TextField
                                    fullWidth
                                    variant="filled"
                                    type="email"
                                    label="Email"
                                    value={values.email}
                                    name="email"
                                    error={!!touched.email && !!errors.email}
                                    helperText={touched.email && errors.email}
                                    onBlur={handleBlur}
                                    onChange={handleChange}
                                    slotProps={{ input: { readOnly: true } }}
                                    sx={{ gridColumn: "span 6" }}
                                />
                                <TextField
                                    fullWidth
                                    variant="filled"
                                    type="text"
                                    label="Contact Tel"
                                    onBlur={handleBlur}
                                    onChange={handleChange}
                                    value={values.contactTel}
                                    name="contactTel"
                                    error={!!touched.contactTel && !!errors.contactTel}
                                    helperText={touched.contactTel && errors.contactTel}
                                    slotProps={{ input: { readOnly: true } }}
                                    sx={{ gridColumn: "span 6" }}
                                />
                                <TextField
                                    fullWidth
                                    variant="filled"
                                    type="password"
                                    label="Password"
                                    onBlur={handleBlur}
                                    onChange={handleChange}
                                    value={values.password}
                                    name="password"
                                    error={!!touched.password && !!errors.password}
                                    helperText={touched.password && errors.password}
                                    sx={{ gridColumn: "span 6" }}
                                />          
                                <TextField
                                    fullWidth
                                    variant="filled"
                                    type="password"
                                    label="Confirm Password"
                                    value={values.confirmPassword}
                                    name="confirmPassword"
                                    error={!!touched.confirmPassword && !!errors.confirmPassword}
                                    helperText={touched.confirmPassword && errors.confirmPassword}
                                    onBlur={handleBlur}
                                    onChange={handleChange}
                                    sx={{ gridColumn: "span 6" }}
                                />                         
                                
                                <Button
                                    fullWidth
                                    type="submit"
                                    variant="contained"
                                    color="secondary"
                                    sx={{ gridColumn: "span 12", height: 42 }}                                           
                                >
                                    {crewCode ? "Update" : "Create"}
                                </Button>  
                            </Box>                            
                        </form>
                        )}
                    </Formik>
                </Box>
            )}
        </Box>
    );
};

export default CrewMember;
